import React, { useState, useEffect } from "react";
import {
  FaBolt,
  FaStar,
  FaTrash,
  FaArrowLeft,
  FaExclamationTriangle,
  FaHeart,
  FaPhone,
} from "react-icons/fa";
// --- IMPORTS ---
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { getLoggedUser } from "../controllers/AuthController";
import LogoSlider from "../components/LogoSlider";
import SeoRanking from "../components/SeoRanking";
import { Helmet, HelmetProvider } from "react-helmet-async";
import Header from "./header";
import ProductDetailsView from "./ProductDetailsView";
import CartPage from "./CartPage";
import Footer from "./Footer";

// --- CONFIGURATION ---
const API_BASE = process.env.REACT_APP_API_URL;

// --- STYLES ---
const styles = {
  cardPriceBlock: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  cardPriceRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  cartBtn: {
    flex: 1,
    background: "linear-gradient(90deg, rgb(13, 67, 133), rgb(48 113 189))",
    color: "white",
    border: "none",
    borderRadius: "14px",
    fontSize: "18px",
    fontWeight: "700",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    boxShadow: "0 8px 20px -5px rgba(88, 90, 88, 0.4)",
    transition: "0.2s",
    padding: "16px",
    minWidth: "200px",
  },
  cardPrice: {
    fontSize: "22px",
    fontWeight: "800",
    color: "#0F172A",
  },
  cardMrp: {
    fontSize: "14px",
    textDecoration: "line-through",
    color: "#94A3B8",
  },
  cardDiscount: {
    background: "#DCFCE7",
    color: "#166534",
    padding: "2px 6px",
    borderRadius: "4px",
    fontSize: "11px",
    fontWeight: "700",
  },
  cardVariants: {
    fontSize: "12px",
    color: "#d60909",
    display: "flex",
    gap: "6px",
    flexWrap: "wrap",
  },

  // Main Wrapper (Full Width, Flex Column)
  pageWrapper: {
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    minHeight: "100vh",
    color: "#0F172A",
    display: "flex",
    flexDirection: "column",
    backgroundColor: "#F8FAFC",
    overflowX: "hidden",
  },

  // 80% Width Workspace
  workspace: {
    width: "100%",
    maxWidth: "1600px",
    margin: "0 auto",
    marginTop: "80px", // Account for fixed header
    flex: 1, // Pushes footer down
    boxSizing: "border-box",
  },

  // 4x4 Grid Layout
  gridLayout: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "25px",
    marginBottom: "50px",
  },

  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end",
    marginBottom: "25px",
    marginTop: "10px",
  },
  secTitle: {
    fontSize: "28px",
    fontWeight: "800",
    color: "#0F172A",
    letterSpacing: "-0.5px",
    marginBottom: "4px",
  },
  secDesc: {
    fontSize: "15px",
    color: "#64748B",
    fontWeight: "500",
    width: "100%",
    maxWidth: "1500px",
    paddingRight: "30px",
    boxSizing: "border-box",
  },
  viewAll: {
    fontSize: "14px",
    fontWeight: "700",
    color: "#D60000",
    cursor: "pointer",
    textDecoration: "none",
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },

  // Carousel
  carouselContainer: {
    width: "100vw",
    maxWidth: "100vw",
    marginLeft: "calc(-50vw + 50%)",
    overflow: "hidden",
    // NOTE: Do NOT set height here – it's controlled via CSS media queries
    borderRadius: "24px",
    boxShadow: "0 20px 40px -10px rgba(0,0,0,0.1)",
    position: "relative",
    overflow: "hidden",
    backgroundColor: "#E2E8F0",
    zIndex: 1, // Ensure carousel is visible
    display: "block", // Default: visible
  },
  carouselTrack: {
    display: "flex",
    transition: "transform 0.10s cubic-bezier(0.25, 1, 0.5, 1)",
    width: "100%",
  },
  carouselSlide: {
    minWidth: "100%",
    height: "100%",
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  carouselImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    objectPosition: "center",
    display: "block",
    minHeight: "100%",
    minWidth: "100%",
  },

  // States
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "80px 0",
    textAlign: "center",
  },
  loadingBox: {
    height: "60vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    color: "#64748B",
  },

  // Footer Wrapper
  footerWrapper: { width: "100%", marginTop: "auto" },
};
const categoryBannerMap = {
  residential: 0,
  commercial: 1,
  highway: 2,
};

const bannerCategoryMap = {
  0: "residential",
  1: "commercial",
  2: "highway",
};

// --- PRODUCT CARD COMPONENT ---
const ProductCard = ({
  item,
  onClick,
  addToCart,
  toggleWishlist,
  isWishlisted,
  isWishlistMode,
  activeCategory,
}) => {
  const navigate = useNavigate();

  const slugify = (text) => {
    return text
      .toLowerCase()
      .trim()
      .replace(/&/g, "and") // optional
      .replace(/[^a-z0-9]+/g, "-") // replace spaces & special chars with -
      .replace(/^-+|-+$/g, ""); // remove starting/ending -
  };

  const openProductDetails = () => {
    const slug = `${slugify(item.name)}-${slugify(item.connectivity || "")}`;
    navigate(`/product-details/${slug}/${item.id}`);
  };

  const [hover, setHover] = useState(false);
  const imageSource = item.image || null;
  const power = item.power?.toLowerCase().replace(/\s/g, "");
  const isSpecialPower = ["120kw", "240kw", "60kw"].includes(power);
  return (
    <div
      onClick={openProductDetails}
      style={{
        background: "white",
        borderRadius: "20px",
        overflow: "hidden",
        transition: "all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)",
        position: "relative",
        transform: hover ? "translateY(-8px)" : "translateY(0)",
        boxShadow: hover
          ? "0 15px 30px rgba(0,0,0,0.1)"
          : "0 2px 10px rgba(0,0,0,0.04)",
        cursor: "pointer",
        border: "1px solid #F1F5F9",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className="fade-in"
    >
      {/* Image Area */}
      <div
        style={{
          height: "200px",
          background: "#F8FAFC",
          position: "relative",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: "20px",
          overflow: "hidden",
        }}
      >
        {item.spec && !isWishlistMode && (
          <div
            style={{
              position: "absolute",
              top: "15px",
              left: "15px",
              background: "rgba(255,255,255,0.9)",
              backdropFilter: "blur(4px)",
              padding: "4px 10px",
              borderRadius: "6px",
              fontSize: "11px",
              fontWeight: "700",
              color: "#1E293B",
              zIndex: 2,
            }}
          >
            {item.spec}
          </div>
        )}
        <div
          onClick={(e) => {
            e.stopPropagation();
            toggleWishlist(item);
          }}
          style={{
            position: "absolute",
            top: "15px",
            right: "15px",
            width: "32px",
            height: "32px",
            borderRadius: "50%",
            background: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 4px 10px rgba(0,0,0,0.05)",
            color: isWishlistMode
              ? "rgb(13 67 133),"
              : isWishlisted
                ? "rgb(13 67 133),"
                : "#94A3B8",
            zIndex: 2,
            cursor: "pointer",
            transition: "0.2s",
          }}
        >
          {isWishlistMode ? (
            <FaTrash style={{ fontSize: "12px" }} />
          ) : (
            <FaHeart
              className={isWishlisted ? "heart-anim" : ""}
              style={{ fontSize: "14px" }}
            />
          )}
        </div>
        {imageSource ? (
          <img
            src={imageSource}
            alt={item.name}
            style={{
              maxHeight: "100%",
              maxWidth: "100%",
              objectFit: "contain",
              transition: "transform 0.5s ease",
              transform: hover ? "scale(1.05)" : "scale(1)",
            }}
          />
        ) : (
          <FaBolt style={{ fontSize: "60px", color: "#CBD5E1" }} />
        )}
      </div>
      {/* Info Area */}
      <div
        onClick={openProductDetails}
        style={{
          padding: "18px",
          display: "flex",
          flexDirection: "column",
          flex: 1,
          justifyContent: "space-between",
        }}
      >
        {/* Top Content */}
        <div>
          {/* Rating */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "6px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                fontSize: "12px",
                fontWeight: 700,
                color: "#F59E0B",
              }}
            >
              <FaStar /> {item.rating || 4.5}
            </div>
          </div>
          <h3 style={{ fontSize: "15px", fontWeight: 700 }}>{item.name}</h3>

          {/* Description */}
          {item.description && (
            <div
              style={{
                fontSize: "13px",
                color: "#475569",
                lineHeight: "1.5",
                overflow: "hidden",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
              }}
            >
              {item.description}
            </div>
          )}
        </div>

        {/* Bottom Price Section */}
        <div
          style={{
            marginTop: "12px",
            borderTop: "1px dashed #F1F5F9",
            paddingTop: "12px",
          }}
        >
          <div style={styles.cardPriceBlock}>
            {/* Price Row */}

            {!["120kw", "240kw", "60kw"].includes(
              item.power?.toLowerCase().replace(/\s/g, ""),
            ) ? (
              <div style={styles.cardPriceRow}>
                <div style={styles.cardPrice}>
                  ₹{Number(item?.price ?? 0).toLocaleString()}
                </div>

                {Number(item?.mrp_price ?? 0) > Number(item?.price ?? 0) && (
                  <>
                    <div style={styles.cardMrp}>
                      ₹{Number(item?.mrp_price ?? 0).toLocaleString()}
                    </div>
                    <div style={styles.cardDiscount}>
                      {/* {item?.max_discount ?? 0}% OFF */}
                      {Math.round(
                        ((Number(item?.mrp_price ?? item?.mrp_price ?? 0) -
                          Number(item?.price ?? item?.price ?? 0)) /
                          Number(item?.mrp_price ?? item?.mrp_price ?? 1)) *
                          100,
                      )}
                      % OFF
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div></div>
            )}

            {/* Variants / Connectivity */}
            {(item.connectivity || item.total_variants) && (
              <div style={styles.cardVariants}>
                {item.connectivity && <span>{item.connectivity}</span>}
                {item.total_variants > 1 && (
                  <span>+ {item.total_variants - 1} more</span>
                )}
              </div>
            )}
          </div>
        </div>
        <div style={{ marginTop: "10px" }}>
          <h5
            style={{
              fontSize: "13px",
              fontWeight: 600,
              color: "#0F172A",
              margin: 0,
            }}
          >
            Compatibility
          </h5>
        </div>

        <div
          style={{
            fontSize: "12px",
            marginTop: "4px",
            display: "flex",
            gap: "8px",
            alignItems: "center",
            flexWrap: "wrap",
            color: "#475569",
          }}
        >
          {item.applications ? item.applications : "-"}
        </div>
        <div
          style={{
            fontSize: "12px",
            marginTop: "4px",
            display: "flex",
            gap: "8px",
            alignItems: "center",
            flexWrap: "wrap",
            color: "#475569",
            justifyContent: "center",
          }}
        >
          <img
            src={item.compatibility_img ? item.compatibility_img : ""}
            alt="charger"
            style={{
              width: "360px",
              height: "50px",
              objectFit: "contain",
            }}
          />
          <button
            onClick={openProductDetails}
            style={{
              ...styles.cartBtn,
              marginTop: "10px",
            }}
          >
            {isSpecialPower ? "Call for price" : "Buy Now"}
          </button>
        </div>
      </div>
    </div>
  );
};

const seoData = {
  residential: {
    title: "EV Charger Manufacturer in India | Tucker EV Charger – CGRMART",
    description:
      "CGRMART by Tucker EV Charger is a leading EV charger manufacturer in India offering commercial and DC fast charging solutions for businesses and highways",
    keywords: "home EV charger, residential EV charger, EV charging at home",
  },

  commercial: {
    title: "Buy Commercial EV Charging Stations for Businesses | CGRMART",
    description:
      "Explore commercial EV charging solutions for offices, malls, and business infrastructure across India.",
    keywords:
      "commercial EV charger, business charging station, EV infrastructure India",
  },

  highway: {
    title:
      "Buy DC Fast Chargers Online in India | Highway EV Charging | CGRMART",
    description:
      "High-speed DC fast chargers for highways and fleet operations. Reliable EV charging infrastructure for long-distance travel.",
    keywords: "DC fast charger, highway EV charging, fleet charging India",
  },
};

// --- MAIN TUCKER STORE ---
const TuckerStore = () => {
  const navigate = useNavigate();
  const [currentView, setCurrentView] = useState("home");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [user, setUser] = useState(null);
  const location = useLocation();
  // --- INIT ---
  useEffect(() => {
    const initUser = async () => {
      const loggedUser = getLoggedUser();
      const storedUserData = localStorage.getItem("userData");
      let uId = null;

      if (storedUserData) {
        const u = JSON.parse(storedUserData);
        uId = u.contact || u.email;
      } else if (loggedUser) {
        uId = loggedUser;
      }
    };
    initUser();
  }, []);

  const nextSlide = () => {
    setCurrentBannerIndex((prev) =>
      prev === banners.length - 1 ? 0 : prev + 1,
    );
  };

  const prevSlide = () => {
    setCurrentBannerIndex((prev) =>
      prev === 0 ? banners.length - 1 : prev - 1,
    );
  };

  const { category } = useParams();

  useEffect(() => {
    if (category) {
      setActiveCategory(category.toLowerCase());
    }
  }, [category]);

  useEffect(() => {
    const loadUser = () => {
      const storedData = localStorage.getItem("userData");
      if (storedData) {
        setUser(JSON.parse(storedData));
      } else {
        setUser(null);
      }
    };

    loadUser();
  }, []);

  // Initial cart load
  const [cartCount, setCartCount] = useState(() => {
    const savedCart = JSON.parse(localStorage.getItem("cart")) || [];
    return savedCart.reduce((sum, item) => sum + (item.quantity || 1), 0);
  });

  const [banners, setBanners] = useState([]);
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
  const [activeCategory, setActiveCategory] = useState(
    localStorage.getItem("activeCategory") || "residential",
  );
  const [usingMock, setUsingMock] = useState(false);

  // --- API FETCH ---
  const [allProducts, setAllProducts] = useState([]);
  const [products, setProducts] = useState([]);
  const [wishlistIds, setWishlistIds] = useState(new Set());
  const [loading, setLoading] = useState(false);

  const USER_ID = user?.email;

  useEffect(() => {
    if (categoryBannerMap[activeCategory] !== undefined) {
      setCurrentBannerIndex(categoryBannerMap[activeCategory]);
    }
  }, [activeCategory, banners]);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);

        // Load wishlist from localStorage
        const localWishlist =
          JSON.parse(localStorage.getItem("wishlist")) || [];
        setWishlistIds(new Set(localWishlist));

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 3000);

        try {
          // Always fetch banners and products
          const bannerPromise = fetch(`${API_BASE}/get-banner`, {
            signal: controller.signal,
          });
          const productPromise = fetch(`${API_BASE}/get-productList`, {
            signal: controller.signal,
          });

          // Only fetch wishlist if USER_ID exists
          const wishlistPromise = USER_ID
            ? fetch(`${API_BASE}/get-wishlist`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ user_identifier: USER_ID }),
                signal: controller.signal,
              })
            : Promise.resolve({
                json: async () => ({ success: false, data: [] }),
              }); // fallback empty response

          const [bannerRes, productRes, wishlistRes] = await Promise.all([
            bannerPromise,
            productPromise,
            wishlistPromise,
          ]);

          clearTimeout(timeoutId);

          const bannerData = await bannerRes.json();
          const productData = await productRes.json();
          const wishlistData = await wishlistRes.json();

          if (bannerData.success) setBanners(bannerData.data);

          if (productData.success && Array.isArray(productData.data)) {
            const formatted = productData.data.map((p) => {
              const connectivity = p.productname
                ?.split(/\s+[–-]\s+/) // ✅ requires spaces around dash
                .pop()
                .trim();

              return {
                id: p.productid,
                name: p.variant_name,
                price: p.price,
                mrp_price: p.mrp_price,
                rating: p.rating,
                power: p.power,
                category: p.product_usage?.toLowerCase(),
                spec: `${p.power} ${p.Type}`,
                image: p.imageurl,
                description: p.description,
                total_variants: p.total_variants,
                applications: p.applications,
                connectivity, // ✅ Wi-Fi / RFID / Wi-Fi + GSM + RFID
                compatibility_img: p.compatibility_img,
              };
            });

            setAllProducts(formatted);
          }

          if (wishlistData.success) {
            setWishlistIds(
              new Set(
                wishlistData.data.map((i) => i.product_id || i.productid),
              ),
            );
          }
        } catch (err) {
          console.warn("API failed → using MOCK data");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, [USER_ID]);

  useEffect(() => {
    const category = activeCategory?.toLowerCase();

    const filtered = allProducts.filter((item) => item.category === category);
    setProducts(filtered);
  }, [activeCategory, allProducts]);

  // --- Toggle Wishlist ---
  const toggleWishlist = async (product) => {
    const isLiked = wishlistIds.has(product.id);

    setWishlistIds((prev) => {
      const newSet = new Set(prev);
      if (isLiked) newSet.delete(product.id);
      else newSet.add(product.id);
      localStorage.setItem("wishlist", JSON.stringify([...newSet]));

      //Notify Header that wishlist changed
      window.dispatchEvent(new Event("wishlistUpdated"));
      return newSet;
    });

    if (!usingMock && USER_ID) {
      //Only call API if USER_ID exists
      try {
        const url = isLiked
          ? `${API_BASE}/remove-from-wishlist`
          : `${API_BASE}/add-to-wishlist`;
        const body = {
          product_id: product.id,
          product_name: product.name,
          user_identifier: USER_ID,
        };
        fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }).catch((e) => console.error("Wishlist sync failed", e));
      } catch (error) {
        console.error("API error:", error);
      }
    }
  };

  const handleProductClick = (product) => {
    setSelectedProduct(product);
    setCurrentView("product-details");
    window.scrollTo(0, 0);
  };

  const refreshCartCount = () => {
    const savedCart = JSON.parse(localStorage.getItem("cart")) || [];
    const count = savedCart.reduce(
      (sum, item) => sum + (item.quantity || 1),
      0,
    );
    setCartCount(count);
  };

  const handleAddToCart = () => {
    setCartCount((prev) => prev + 1);
  };

  // useEffect(() => {
  //   if (banners.length > 1) {
  //     const interval = setInterval(() => {
  //       setCurrentBannerIndex((prev) =>
  //         prev === banners.length - 1 ? 0 : prev + 1,
  //       );
  //     }, 4000);
  //     return () => clearInterval(interval);
  //   }
  // }, [banners]);
  const currentSEO = seoData[activeCategory] || seoData["residential"];

  // --- RENDER HELPERS ---
  const renderStoreSection = (
    title,
    desc,
    category,
    footer_title,
    footer_content,
    images,
  ) => {
    const items = products.filter((p) => p.category === category);
    if (items.length === 0) return null;

    return (
      <div style={{ marginBottom: "80px" }} className="fade-in">
        <div style={styles.sectionHeader}>
          <div></div>
        </div>
        <br></br>
        {/* The 4x4 Grid */}
        <div style={styles.gridLayout} className="grid-layout">
          {items.map((item) => (
            <ProductCard
              key={item.id}
              item={item}
              onClick={() => handleProductClick(item)}
              addToCart={handleAddToCart}
              toggleWishlist={toggleWishlist}
              isWishlisted={wishlistIds.has(item.id)}
              activeCategory={activeCategory}
            />
          ))}
        </div>
      </div>
    );
  };
  const handleCategoryChange = (cat) => {
    setActiveCategory(cat);

    // ✅ Change banner based on category
    if (categoryBannerMap[cat] !== undefined) {
      setCurrentBannerIndex(categoryBannerMap[cat]);
    }

    navigate(`/category/${cat}`);
  };
  return (
    <div style={styles.pageWrapper}>
      {/* GLOBAL ANIMATION & MEDIA QUERIES */}
      <style>{`
        .fade-in { animation: fadeIn 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards; } 
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } } 
        @keyframes heartPulse { 0% { transform: scale(1); } 50% { transform: scale(1.3); } 100% { transform: scale(1); } } 
        .heart-anim { animation: heartPulse 0.4s ease-out; color: #D60000 !important; }
        
        /* Responsive Grid fixes */
        @media (max-width: 1400px) {
          .grid-layout { grid-template-columns: repeat(3, 1fr) !important; }
        }
        @media (max-width: 1100px) {
          .grid-layout { grid-template-columns: repeat(2, 1fr) !important; }
        }
        
        /* Carousel Responsive Styles */
        // /* Base carousel height for desktop-ish screens */
        // .carouselContainer {
        //   height: 420px;
        // }

        /* Hide carousel on mobile (320px to 748px) */
        @media (min-width: 320px) and (max-width: 748px) {
          .carouselContainer { 
            display: none !important; 
          }
        }
        
        /* Show carousel from 748px to 1200px */
        @media (min-width: 748px) and (max-width: 1200px) {
          .carouselContainer { 
            height: 280px !important; 
            border-radius: 20px !important;
          }
          .carouselContainer img { 
            width: 100% !important; 
            height: 100% !important; 
            object-fit: cover !important; 
            object-position: center !important; 
          }
        }
        
        /* Show carousel from 1200px to 1500px */
        @media (min-width: 1200px) and (max-width: 1500px) {
          .carouselContainer { 
            height: 320px !important; 
            border-radius: 22px !important;
          }
          .carouselContainer img { 
            width: 100% !important; 
            height: 100% !important; 
            object-fit: cover !important; 
            object-position: center !important; 
          }
        }
        
        /* Show carousel from 1500px to 1920px */
        @media (min-width: 1500px) and (max-width: 1920px) {
          .carouselContainer { 
            /* height: 420px !important; */
            height: 800px !important;
            border-radius: 24px !important;
          }
          .carouselContainer img { 
            width: 100% !important; 
            height: 100% !important; 
            object-fit: cover !important; 
            object-position: center !important; 
          }
        }
        
        /* Show carousel for desktop (1920px and above) */
        @media (min-width: 1920px) {
          .carouselContainer { 
            /* height: 420px !important; */
            height: 600px !important;
            border-radius: 24px !important;
          }
          .carouselContainer img { 
            width: 100% !important; 
            height: 100% !important; 
            object-fit: cover !important; 
            object-position: center !important; 
          }
        }
        
        @media (max-width: 768px) {
          .grid-layout { grid-template-columns: repeat(2, 1fr) !important; gap: 15px !important; }
          .workspace { width: 95% !important; padding: 20px 0 !important; margin-top: 90px !important; }
          .secTitle { font-size: 22px !important; }
          .secDesc { font-size: 14px !important; }
        }
        @media (max-width: 600px) {
          .grid-layout { grid-template-columns: 1fr !important; gap: 15px !important; }
          .workspace { width: 95% !important; padding: 15px 0 !important; margin-top: 85px !important; }
          .product-spec-tag { font-size: 10px !important; padding: 3px 6px !important; }
          .product-heart { width: 28px !important; height: 28px !important; }
          .product-price { font-size: 20px !important; }
          .header-categories { flex-wrap: wrap !important; gap: 8px !important; }
        }
      `}</style>

      {/* --- CONDITIONAL VIEWS --- */}

      {/* 1. PRODUCT DETAILS VIEW */}
      {currentView === "product-details" && selectedProduct ? (
        <ProductDetailsView
          product={selectedProduct}
          onBack={() => setCurrentView("residential")}
          addToCart={() => {
            handleAddToCart();
            refreshCartCount(); // Ensure sync
          }}
          toggleWishlist={toggleWishlist}
          isWishlisted={wishlistIds.has(selectedProduct.id)}
        />
      ) : /* 2. CART PAGE VIEW */
      currentView === "cart" ? (
        <CartPage
          onBack={() => setCurrentView("residential")}
          onCartUpdate={refreshCartCount} // Pass update function to cart
        />
      ) : (
        /* 3. HOME & WISHLIST VIEWS (Shared Layout) */
        <>
          <Helmet>
            <title>{currentSEO.title}</title>

            <meta name="description" content={currentSEO.description} />
            <meta name="keywords" content={currentSEO.keywords} />
            <meta name="robots" content="index, follow" />

            {/* Open Graph */}
            <meta property="og:title" content={currentSEO.title} />
            <meta property="og:description" content={currentSEO.description} />
            <meta property="og:type" content="website" />
            <meta
              property="og:url"
              content={`https://cgrmart.com/category/${activeCategory}`}
            />

            {/* Twitter */}
            <meta name="twitter:title" content={currentSEO.title} />
            <meta name="twitter:description" content={currentSEO.description} />

            {/* Canonical */}
            <link
              rel="canonical"
              href={`https://cgrmart.com/category/${activeCategory}`}
            />
          </Helmet>

          {/* HEADER (Always Visible) */}
          <Header
            currentView={currentView}
            activeCategory={activeCategory}
            onNavigate={(view) => {
              setCurrentView(view);
              window.scrollTo(0, 0);
            }}
            onCategoryChange={handleCategoryChange}
            cartCount={cartCount}
            wishlistCount={wishlistIds.size}
          />
          {/* Main Content Area (80% Width) */}
          <div style={styles.workspace}>
            {/* Offline Warning */}
            {usingMock && (
              <div
                style={{
                  background: "#FEF2F2",
                  border: "1px solid #FECACA",
                  color: "#B91C1C",
                  padding: "12px",
                  borderRadius: "12px",
                  marginBottom: "30px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  fontSize: "13px",
                  fontWeight: "600",
                }}
              >
                <FaExclamationTriangle /> Demo Mode: Showing sample products
              </div>
            )}

            {/* LOADING STATE */}
            {loading ? (
              <div style={styles.loadingBox}>
                <FaBolt
                  className="fa-spin"
                  style={{
                    fontSize: "50px",
                    color: "#D60000",
                    animation: "spin 1s linear infinite",
                    marginBottom: "20px",
                  }}
                />
                <div>Loading Tucker Store...</div>
                <style>{`@keyframes spin { 100% { transform: rotate(360deg); } }`}</style>
              </div>
            ) : /* CONTENT SWITCH */
            currentView === "wishlist" ? (
              /* WISHLIST VIEW */
              <div className="fade-in">
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: "40px",
                    gap: "15px",
                  }}
                >
                  <button
                    onClick={() => setCurrentView("residential")}
                    style={{
                      background: "white",
                      border: "1px solid #E2E8F0",
                      width: "40px",
                      height: "40px",
                      borderRadius: "50%",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <FaArrowLeft />
                  </button>
                  <h1>My Wishlist</h1>
                </div>
                {products.filter((p) => wishlistIds.has(p.id)).length === 0 ? (
                  <div style={styles.emptyState}>Your wishlist is empty</div>
                ) : (
                  <div style={styles.gridLayout} className="grid-layout">
                    {products
                      .filter((p) => wishlistIds.has(p.id))
                      .map((item, i) => (
                        <ProductCard
                          key={i}
                          item={item}
                          onClick={() => handleProductClick(item)}
                          addToCart={handleAddToCart}
                          toggleWishlist={toggleWishlist}
                          isWishlisted={true}
                          isWishlistMode={true}
                          activeCategory={activeCategory}
                        />
                      ))}
                  </div>
                )}
              </div>
            ) : (
              /* HOME VIEW */
              <>
                {/* 🔥 Slider here */}
                <SeoRanking />
                {/* {banners.length > 0 && (
                  <div
                    style={styles.carouselContainer}
                    className="fade-in carouselContainer"
                  >
                    <div style={styles.carouselSlide}>
                      {banners.length > 0 && (
                        <img
                          src={banners[0].banner_img}
                          alt="banner"
                          style={{
                            ...styles.carouselImage,
                            cursor: "pointer",
                          }}
                          onClick={() => {
                            const category = bannerCategoryMap[0];

                            if (category) {
                              setActiveCategory(category);
                              navigate(`/category/${category}`);
                            }
                          }}
                        />
                      )}
                    </div>
                  </div>
                )} */}
                {banners.length > 0 && (
                  <div
                    style={styles.carouselContainer}
                    className="fade-in carouselContainer"
                  >
                    {/* 🔥 Track */}
                    <div
                      style={{
                        ...styles.carouselTrack,
                        transform: `translateX(-${currentBannerIndex * 100}%)`,
                      }}
                    >
                      {banners.map((banner, index) => (
                        <div key={index} style={styles.carouselSlide}>
                          <img
                            src={banner.banner_img}
                            alt={`banner-${index}`}
                            style={styles.carouselImage}
                          />
                        </div>
                      ))}
                    </div>

                    {/* 🔥 PREVIOUS BUTTON */}
                    <button
                      onClick={prevSlide}
                      style={{
                        position: "absolute",
                        left: "20px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        background: "rgba(0,0,0,0.5)",
                        color: "#fff",
                        border: "none",
                        borderRadius: "50%",
                        width: "45px",
                        height: "45px",
                        cursor: "pointer",
                        fontSize: "18px",
                        zIndex: 5,
                      }}
                    >
                      ❮
                    </button>

                    {/* 🔥 NEXT BUTTON */}
                    <button
                      onClick={nextSlide}
                      style={{
                        position: "absolute",
                        right: "20px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        background: "rgba(0,0,0,0.5)",
                        color: "#fff",
                        border: "none",
                        borderRadius: "50%",
                        width: "45px",
                        height: "45px",
                        cursor: "pointer",
                        fontSize: "18px",
                        zIndex: 5,
                      }}
                    >
                      ❯
                    </button>
                  </div>
                )}
                {renderStoreSection(
                  "EV Charger Manufacturer in India – Tucker EV Charger",
                  "Residential & Home EV Charging Solutions. Install smart home EV chargers with fast, safe, and efficient charging. Our AC EV chargers support 7kW, 11kW, and 22kW, ideal for residential EV charging, apartments, and villas. OCPP-enabled with mobile app, smart monitoring, and easy installation. Level 2 AC chargers for your garage.Buy home EV chargers online in India with advanced safety, smart connectivity, and fast charging performance. Our residential AC EV chargers support 7kW, 11kW, and 22kW power output, making them ideal for apartments, villas, and independent homes. These chargers come with OCPP-enabled mobile app control, real-time monitoring, and easy installation for daily electric vehicle charging.",
                  "residential",
                  "EV Charger Manufacturer in India – Tucker EV Charger",
                  "CGRMART, powered by Tucker EV Charger, is a trusted EV charger manufacturer in India offering high-quality charging solutions for commercial and highway applications.We provide advanced EV charging systems including commercial chargers and DC fast chargers designed for efficiency, durability, and fast performance. Our solutions are ideal for businesses, apartments, offices, and highway charging stations.With a focus on innovation and reliability, Tucker EV Charger supports the growing demand for electric vehicle infrastructure across Chennai, Tamil Nadu, and India.Choose CGRMART for scalable, future-ready EV charging solutions tailored to your needs.",
                  "Animation_2.gif",
                )}
                {renderStoreSection(
                  "Commercial EV Chargers",
                  "Commercial EV Charging Station .Reliable commercial EV charging infrastructure with AC and DC fast chargers. Perfect for shopping malls, offices, parking lots, and fleet operators. Supports OCPP, payment integration, and smart energy management.Smart commercial EV chargers for offices, malls, hotels, and fleet parking. OCPP-enabled AC & DC EV charging stations with billing, RFID, and remote monitoring. Ideal for public and workplace EV charging.",
                  "commercial",
                  "Commercial EV Charger Solutions for Businesses",
                  "CGRMART, powered by Tucker EV Charger, offers reliable commercial EV charger solutions designed for businesses, apartments, offices, and public spaces across India.Our commercial EV chargers are built for efficiency, safety, and long-term performance, making them ideal for high-usage environments. We provide scalable EV charging infrastructure that supports multiple vehicles with smart charging features.From AC EV chargers for daily use to advanced solutions for commercial applications, our systems are designed to meet the growing demand for electric vehicle charging in Chennai, Tamil Nadu, and across India.Choose Tucker EV Charger for dependable commercial EV charging solutions tailored to your business needs.",
                  "Animation_2.gif",
                )}
                {renderStoreSection(
                  "DC Fast Chargers for Highway EV Charging",
                  "High-power DC fast charger 30kW–240kW for highway EV charging. Fast, reliable, and smart public EV charging solution. Ultra-fast DC EV charging station designed for highway, corridor, and public charging. Ideal for long-distance travel, fleet charging, and commercial deployment. Supports CCS2, OCPP, RFID, and billing. High-speed DC charging infrastructure. Discover high-power DC fast chargers ranging from 30kW to 240kW for highway and fleet EV charging. These ultra-fast charging stations are designed for long-distance travel, public charging corridors, and fleet operations. With support for CCS2, RFID, OCPP, and smart billing systems, they ensure reliable and high-speed EV charging infrastructure.",
                  "highway",
                  "DC Fast Chargers for Highway EV Charging Stations",
                  "CGRMART, powered by Tucker EV Charger, provides high-performance DC fast chargers designed for highway EV charging stations and high-traffic locations across India.Our DC fast chargers deliver rapid charging for electric vehicles, making them ideal for highways, petrol bunks, and public charging infrastructure. Built with advanced technology, these chargers ensure fast, reliable, and efficient performance for continuous usage.We offer scalable EV fast charging solutions including CCS2 fast chargers that support modern electric vehicles and enable quick turnaround times for users on the move.Serving Chennai, Tamil Nadu, and across India, Tucker EV Charger helps businesses build powerful and future-ready highway EV charging infrastructure.Choose CGRMART for high-speed EV charging solutions tailored for highway and commercial applications.",
                  "Animation_2.gif",
                )}
              </>
            )}
          </div>

          {/* 🔥 Slider here */}
          {/* <LogoSlider /> */}

          {/* FOOTER (Full Width) */}
          <div style={styles.footerWrapper}>
            <Footer onCategoryChange={(cat) => setActiveCategory(cat)} />
          </div>
        </>
      )}
    </div>
  );
};

export default TuckerStore;
